/**
 * Copyright (c) 2017 CRF Box Oy. All Rights Reserved. Company Confidential.
 */

import React, { useState, useEffect, useReducer, useRef } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'ramda';
import Loading from '../../../components/loading/loading.component';
import * as consts from '../../../const';
import StudyDetails from './modules/study.details';
import { Pagination, Grid, FlashMessages, Search } from 'tc-client-common';
import ParticipantsService from '../participants.service';
import { OrganizationFilter, ParticipantStatusFilter, ActionsNeededFilter } from './modules/filters';
import { getPlaceholderRows, getLocationMetadata, emptyData } from './modules/utils';
import Notification from '../../common/notification/notification.component';
import debounce from 'lodash/debounce';

const { withFlashProvider, useFlashMessage, flashMessageType } = FlashMessages;
const { usePagination, withPagination } = Pagination;
const PAGE_SIZE = 10;

const Participants = ({
  fetchParticipantStatuses,
  fetchOrganizations,
  getOverallDetails,
  getBatchArchivingStatus,
  participantStudy,
  getParticipants,
  studyGid,
  location,
  match
}) => {
  const isCancelled = useRef(false);
  const paginationApi = usePagination();
  const flashMessageApi = useFlashMessage();

  const [participants, setParticipants] = useState([]);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [nextPageSize, setNextPageSize] = useState(PAGE_SIZE);
  const [emptyParticipants, setEmptyParticipants] = useState(false);

  const [overallStatus, setOverAllStatus] = useState(null);
  const [participantStatuses, setParticipantStatuses] = useState(null);
  const [organizations, setOrganizations] = useState([]);

  const initialFilters = { status: '', sites: [], optionalDocPublished: '', search: '' };
  const [filters, dispatchFilter] = useReducer((state, action) => {
    switch (action.type) {
      case 'status':
        return { ...state, status: action.payload };
      case 'sites':
        return { ...state, sites: action.payload };
      case 'optionalDocPublished':
        return { ...state, optionalDocPublished: action.payload };
      case 'search':
        return { ...state, search: action.payload };
      default:
        throw new Error('Unexpected action');
    }
  }, initialFilters);

  useEffect(() => {
    selectPage({ nextPage: 1, pageSize: PAGE_SIZE, ...filters });

    getParticipantStatuses(studyGid);
    getOrganizations(studyGid);
    getOverallStatus(studyGid);

    return () => {
      isCancelled.current = true;
    };
  }, []);

  const onError = (error) => {
    if (isCancelled.current) return;

    flashMessageApi.add({
      message: consts.PARTICIPANTS_PAGE_ERROR,
      type: flashMessageType.error,
      title: error.message,
      duration: 5000
    });

    paginationApi.loading(false);
    setLoadingParticipants(false);
  };

  const filterAppliedNotification = () => {
    flashMessageApi.add({
      title: consts.FILTERS_APPLIED,
      type: flashMessageType.success,
      message: consts.FILTERS_APPLIED_LIST_REFRESHED,
      duration: 5000
    });
  };

  const getOverallStatus = (studyGid, sites) => {
    setOverAllStatus(null);
    getBatchArchivingStatus(studyGid, sites);

    getOverallDetails(studyGid, sites)
      .then((response) => {
        if (isCancelled.current) return;
        setOverAllStatus(response.data.overallParticipantStatus);
      })
      .catch(onError);
  };

  const getParticipantStatuses = (studyGid) => {
    fetchParticipantStatuses(studyGid)
      .then((response) => {
        if (isCancelled.current) return;

        setParticipantStatuses(response);
      })
      .catch(onError);
  };

  const getOrganizations = (studyGid) => {
    fetchOrganizations(studyGid)
      .then((response) => {
        if (isCancelled.current) return;

        setOrganizations(response);
      })
      .catch(onError);
  };

  const selectPage = ({
    nextPage,
    pageSize,
    status = filters.status,
    sites = filters.sites,
    optionalDocPublished = filters.optionalDocPublished,
    search = filters.search,
    onSuccess = () => {}
  }) => {
    setEmptyParticipants(false);
    paginationApi.show();
    setNextPageSize(pageSize);

    setLoadingParticipants(true);
    paginationApi.loading(true);

    getParticipants({ studyGid, status, sites, optionalDocPublished, search, pageSize, nextPage })
      .then(({ data, itemsCount }) => {
        if (isCancelled.current) return;
        onSuccess();

        if (data.length === 0) {
          setEmptyParticipants(true);
          paginationApi.hide();
        } else {
          // items overflow
          if ((nextPage - 1) * pageSize > itemsCount) {
            selectPage({ nextPage: 1, pageSize, ...filters });
            return;
          }
          setParticipants(data);

          const pageCount = Math.ceil(itemsCount / pageSize);
          paginationApi.update({ pageCount, pageSize, itemsCount, currentPage: nextPage });
        }

        setLoadingParticipants(false);
        paginationApi.loading(false);
      })
      .catch(onError);
  };

  const contentColumnDefinition = [
    { label: ParticipantsService.columns.participantName, size: 16 },
    { label: ParticipantsService.columns.subjectNumber, size: 14 },
    { label: ParticipantsService.columns.site, size: 14 },
    { label: ParticipantsService.columns.status, size: 20 },
    { label: ParticipantsService.columns.latestStatusChange, size: 20 },
    { label: ParticipantsService.columns.actions, size: 16, textAlignment: 'right' }
  ];

  paginationApi.selectPage(selectPage);

  const {
    userManagementOrgFlow,
    userManagementFlow,
    organizationId = '',
    organizationName = ''
  } = getLocationMetadata({ location, match });

  const participantsRows = ParticipantsService.buildParticipantDocumentsContent(
    participantStudy,
    participants,
    userManagementFlow,
    userManagementOrgFlow ? { organizationId, organizationName } : null
  );

  const filtersByStatus = ParticipantsService.createParticipantStatusesSelection(participantStatuses);
  const filtersByOrganizations = ParticipantsService.createOrganizationsSelection(organizations);
  const filtersByOptionalDocuments = ParticipantsService.createOptionalDocumentSelection();

  const onOrganizationChange = ({ value }) => {
    let sites;

    if (value === '') {
      sites = [];
    } else if (filters.sites.includes(value)) {
      sites = filters.sites.filter((v) => v !== value);
    } else {
      sites = filters.sites.concat(value);

      if (sites.length === filtersByOrganizations.length - 1) sites = [];
    }

    dispatchFilter({ type: 'sites', payload: sites });
    getOverallStatus(studyGid, sites);
    selectPage({
      nextPage: 1,
      pageSize: nextPageSize || PAGE_SIZE,
      ...{ ...filters, sites },
      onSuccess: () => filterAppliedNotification()
    });
  };

  const onStatusChange = ({ value }) => {
    dispatchFilter({ type: 'status', payload: value });
    selectPage({
      nextPage: 1,
      pageSize: nextPageSize || PAGE_SIZE,
      ...{ ...filters, status: value },
      onSuccess: () => filterAppliedNotification()
    });
  };

  const onActionsNeededChange = ({ value }) => {
    dispatchFilter({ type: 'optionalDocPublished', payload: value });
    selectPage({
      nextPage: 1,
      pageSize: nextPageSize || PAGE_SIZE,
      ...{ ...filters, optionalDocPublished: value },
      onSuccess: () => filterAppliedNotification()
    });
  };

  const onSearchChange = (value) => {
    dispatchFilter({ type: 'search', payload: value });
    selectPage({
      nextPage: 1,
      pageSize: nextPageSize || PAGE_SIZE,
      ...{ ...filters, search: value },
      onSuccess: () => filterAppliedNotification()
    });
  };

  return (
    <div id="participants-component">
      <div className="organizations-status-selector-wrapper">
        {filtersByOrganizations.length === 0 ? (
          <Loading />
        ) : (
          <OrganizationFilter
            appliedFilters={filters.sites}
            onOrganizationChange={onOrganizationChange}
            filtersByOrganizations={filtersByOrganizations}
          />
        )}
      </div>
      <div className="overall-status-wrapper">
        {!overallStatus ? <Loading /> : <StudyDetails overallStatus={overallStatus} />}
      </div>

      <div className="participant-documents-filters">
        <div className="participant-status-filter-wrapper">
          {filtersByStatus.length === 0 ? (
            <Loading />
          ) : (
            <ParticipantStatusFilter
              appliedFilters={filters}
              onStatusChange={onStatusChange}
              filtersByStatus={filtersByStatus}
            />
          )}
        </div>
        <div className="participant-optional-doc-filter-wrapper">
          {filtersByOptionalDocuments.length === 0 ? (
            <Loading />
          ) : (
            <ActionsNeededFilter
              appliedFilters={filters}
              onActionsNeededChange={onActionsNeededChange}
              filtersByOptionalDocuments={filtersByOptionalDocuments}
            />
          )}
        </div>
        <div className="participant-search-filter-wrapper">
          <Search placeholder={consts.SEARCH_BY_USERNAME_AND_SSN} onChange={debounce(onSearchChange, 500)} />
        </div>
      </div>

      <Grid
        gridId={`participant-documents${emptyParticipants ? '-empty' : ''}`}
        columnDefinition={contentColumnDefinition}
        rows={
          loadingParticipants
            ? getPlaceholderRows(nextPageSize)
            : emptyParticipants
              ? [{ id: 'empty-row', columns: [{ columnData: emptyData }] }]
              : participantsRows
        }
      />
    </div>
  );
};

Participants.propTypes = {
  fetchParticipantStatuses: PropTypes.func,
  fetchOrganizations: PropTypes.func,
  getOverallDetails: PropTypes.func,
  participantStudy: PropTypes.object,
  getParticipants: PropTypes.func,
  getBatchArchivingStatus: PropTypes.func,
  studyGid: PropTypes.string,
  location: PropTypes.object,
  match: PropTypes.object
};

export default compose(withFlashProvider, withPagination)(Participants);
